import React, { useState, useEffect } from 'react';
import { StandalonePublicWebsiteEngine } from './StandalonePublicWebsiteEngine';
import { ErrorBoundary } from '../common/ErrorBoundary';

interface CustomDomainResolverProps {
  hostname?: string;
}

interface ResolveApiResponse {
  success: boolean;
  domain: string;
  websiteId?: string;
  siteId?: string;
  tenantId?: string;
  error?: string;
}

/**
 * CustomDomainResolver (Rule 12 & Rule 35 Compliant):
 * Resolves customer custom domain directly via GET /api/domains/resolve?domain=${hostname}
 * without performing URL redirects or showing noisy technical error messages.
 * Renders an elegant skeleton during resolution, and delegates to StandalonePublicWebsiteEngine (Shared renderer)
 * using the retrieved websiteId. Shows a clean 404 screen if the domain is not mapped.
 */
export const CustomDomainResolver: React.FC<CustomDomainResolverProps> = React.memo(({ hostname }) => {
  const targetHost = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  const [resolvedWebsiteId, setResolvedWebsiteId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const cached = sessionStorage.getItem(`resolved_domain_${targetHost}`);
      return cached || null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(() => !resolvedWebsiteId);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    // If already resolved from fast session cache, still perform silent revalidation
    const resolveDomain = async () => {
      try {
        const cleanHost = targetHost.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
        const res = await fetch(`/api/domains/resolve?domain=${encodeURIComponent(cleanHost)}`);
        
        if (!isMounted) return;

        if (res.ok) {
          const data: ResolveApiResponse = await res.json();
          const siteId = data.websiteId || data.siteId;
          if (siteId) {
            setResolvedWebsiteId(siteId);
            setIsNotFound(false);
            try {
              sessionStorage.setItem(`resolved_domain_${cleanHost}`, siteId);
            } catch {
              // Ignore session storage errors
            }
          } else {
            setIsNotFound(true);
          }
        } else {
          // If server returned 404 or non-200, mark as not found
          setIsNotFound(true);
        }
      } catch (err) {
        console.warn('[CustomDomainResolver] Resolution error:', err);
        // Only set not found if we don't already have a cached ID
        if (!resolvedWebsiteId) {
          setIsNotFound(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    resolveDomain();

    return () => {
      isMounted = false;
    };
  }, [targetHost]);

  // Elegant loading skeleton (Rule 35: No technical jargon or telemetry)
  if (isLoading && !resolvedWebsiteId) {
    return (
      <div className="min-h-screen bg-white flex flex-col animate-pulse select-none">
        {/* Navigation placeholder */}
        <div className="h-16 border-b border-slate-100 px-6 sm:px-12 flex items-center justify-between">
          <div className="w-36 h-7 bg-slate-200 rounded-lg" />
          <div className="hidden md:flex items-center gap-6">
            <div className="w-16 h-4 bg-slate-100 rounded" />
            <div className="w-16 h-4 bg-slate-100 rounded" />
            <div className="w-16 h-4 bg-slate-100 rounded" />
          </div>
          <div className="w-28 h-9 bg-slate-200 rounded-xl" />
        </div>
        {/* Hero placeholder */}
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 w-full flex flex-col items-center text-center space-y-4">
          <div className="w-40 h-5 bg-slate-100 rounded-full" />
          <div className="w-3/4 max-w-md h-10 bg-slate-200 rounded-xl mt-2" />
          <div className="w-1/2 max-w-sm h-4 bg-slate-100 rounded-lg" />
          <div className="w-32 h-10 bg-slate-200 rounded-xl mt-6" />
        </div>
      </div>
    );
  }

  // Not Found Screen (404)
  if (isNotFound || !resolvedWebsiteId) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-6 text-center select-none font-sans">
        <div className="w-16 h-16 rounded-3xl bg-slate-800/80 border border-slate-700/60 flex items-center justify-center mb-5 text-2xl font-bold text-slate-400">
          404
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-3">
          Web Sitesi Bulunamadı
        </h1>
        <p className="text-sm text-slate-400 max-w-md mb-8 leading-relaxed">
          Bu alan adı ({targetHost}) henüz yayınlanmış bir web sitesiyle eşleştirilmemiş veya DNS yönlendirmesi tamamlanmamış olabilir.
        </p>
      </div>
    );
  }

  // Render the website directly using StandalonePublicWebsiteEngine without redirecting the browser URL
  return (
    <ErrorBoundary
      fallbackTitle="Web Sitesi Yüklenirken Bir Hata Oluştu"
      fallbackDescription="Sayfa içeriği görüntülenirken beklenmeyen bir hata meydana geldi. Lütfen sayfayı yenileyiniz."
    >
      <StandalonePublicWebsiteEngine initialTarget={resolvedWebsiteId} />
    </ErrorBoundary>
  );
});
CustomDomainResolver.displayName = 'CustomDomainResolver';
export default CustomDomainResolver;
